// Editor for a single release_item row. Inline edit — category dropdown,
// text, rollout override, parity notes, Linear tickets, and a searchable
// flag picker. Save PATCHes; delete removes.
//
// Layout is intentionally tight: category + text + rollout override on one
// row, meta fields on a second row, flag picker at the bottom. Keeps a
// long list of items scannable.
import { useState } from "react";
import type { ItemCategory, ReleaseItem } from "@release-hub/shared";
import { useDeleteItem, usePatchItem } from "@/api/mutations";
import { ApiError } from "@/api/client";
import { Button } from "@/ui/button";
import { useConfirm } from "@/ui/confirm";
import { FlagPicker } from "./FlagPicker";

const CATEGORIES: ItemCategory[] = [
  "feature",
  "enhancement",
  "bugfix",
  "maintenance",
];

// Rollout override: null = auto-derive (based on linked flags), 'ga'/'ea'
// = force the label regardless of flag state.
type RolloutOverride = "auto" | "ga" | "ea";

function toOverride(item: ReleaseItem): RolloutOverride {
  return item.rollout_override === "ga" || item.rollout_override === "ea"
    ? item.rollout_override
    : "auto";
}

export function ItemEditor({
  item,
  releaseId,
  onDone,
}: {
  item: ReleaseItem;
  releaseId: string;
  /** Called after a successful save or on Cancel, so a parent row can
   *  collapse back to read mode. Optional — omit for always-open lists. */
  onDone?: () => void;
}) {
  const patch = usePatchItem();
  const del = useDeleteItem();
  const confirm = useConfirm();
  const [category, setCategory] = useState<ItemCategory>(item.category);
  const [text, setText] = useState(item.text);
  const [override, setOverride] = useState<RolloutOverride>(toOverride(item));
  const [parity, setParity] = useState(item.parity_notes ?? "");
  const [flags, setFlags] = useState<string[]>(item.linked_flag_ids);
  const [tickets, setTickets] = useState(item.linear_ticket_ids.join(", "));
  const [err, setErr] = useState<string>("");

  // Tickets are entered in one single-line field; accept commas, whitespace,
  // or newlines as separators so pasted lists split cleanly either way.
  const parseTickets = (s: string) =>
    s.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);

  const dirty =
    category !== item.category ||
    text !== item.text ||
    override !== toOverride(item) ||
    parity !== (item.parity_notes ?? "") ||
    flags.join("\n") !== item.linked_flag_ids.join("\n") ||
    parseTickets(tickets).join(",") !== item.linear_ticket_ids.join(",");

  const save = async () => {
    setErr("");
    try {
      await patch.mutateAsync({
        id: item.id,
        releaseId,
        patch: {
          category,
          text,
          parity_notes: parity.trim() || null,
          linked_flag_ids: flags,
          linear_ticket_ids: parseTickets(tickets),
          rollout_override: override === "auto" ? null : override,
        },
      });
      onDone?.();
    } catch (e) {
      setErr(
        e instanceof ApiError ? e.detail :
        e instanceof Error ? e.message : String(e),
      );
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: "Delete this item?",
      description: (
        <>
          Removes this row from the release. Can be added back manually or
          via Regenerate.
        </>
      ),
      confirmLabel: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await del.mutateAsync({ id: item.id, releaseId });
    } catch (e) {
      setErr(
        e instanceof ApiError ? e.detail :
        e instanceof Error ? e.message : String(e),
      );
    }
  };

  const smallInputCls =
    "rounded border border-border-hover bg-card px-2 py-1 text-xs placeholder:text-text-tertiary focus:border-accent focus:outline-none";

  return (
    <div className="space-y-1.5 rounded-md border border-border bg-card/60 p-2.5">
      {/* Row 1 — category, text, rollout override */}
      <div className="flex flex-wrap items-center gap-1.5">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as ItemCategory)}
          className="rounded border border-border-hover bg-card px-1.5 py-1 text-xs"
        >
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="min-w-0 flex-1 rounded border border-border-hover bg-card px-2 py-1 text-sm focus:border-accent focus:outline-none"
        />
        <RolloutToggle value={override} onChange={setOverride} hasFlags={flags.length > 0} />
      </div>

      {/* Row 2 — parity notes + linear tickets */}
      <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
        <input
          value={parity}
          onChange={(e) => setParity(e.target.value)}
          placeholder="Parity notes (optional)"
          className={smallInputCls}
        />
        <input
          value={tickets}
          onChange={(e) => setTickets(e.target.value)}
          placeholder="Linear tickets — ENG-1234, ENG-5678"
          className={`${smallInputCls} font-mono`}
        />
      </div>

      {/* Row 3 — flag picker */}
      <FlagPicker value={flags} onChange={setFlags} />

      {/* Row 4 — actions */}
      <div className="flex items-center justify-end gap-2 pt-1">
        {err && <span className="text-xs text-destructive">{err}</span>}
        <Button size="sm" variant="ghost" onClick={remove} disabled={del.isPending}>
          Delete
        </Button>
        {onDone && (
          <Button size="sm" variant="ghost" onClick={onDone}>
            Done
          </Button>
        )}
        <Button size="sm" onClick={save} disabled={!dirty || patch.isPending}>
          {patch.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

/** Compact 3-way segmented control for the item's rollout override.
 *  "Auto" = follow the linked flag (or GA if no flag). Explicit GA/EA
 *  overrides regardless. Shows the derived hint next to "Auto" when in
 *  auto mode so the editor sees what will render. */
function RolloutToggle({
  value,
  onChange,
  hasFlags,
}: {
  value: RolloutOverride;
  onChange: (v: RolloutOverride) => void;
  hasFlags: boolean;
}) {
  return (
    <div
      className="inline-flex rounded border border-border-hover bg-bg p-0.5"
      title={
        value === "auto"
          ? hasFlags
            ? "Rollout follows the linked flag(s)"
            : "No flag → GA by default"
          : `Forced to ${value.toUpperCase()} regardless of flag state`
      }
    >
      {(
        [
          ["auto", "Auto"],
          ["ga", "GA"],
          ["ea", "EA"],
        ] as [RolloutOverride, string][]
      ).map(([mode, label]) => (
        <button
          key={mode}
          type="button"
          onClick={() => onChange(mode)}
          className={
            value === mode
              ? "rounded-sm bg-card px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-text shadow-sm"
              : "rounded-sm px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-text-tertiary hover:text-text"
          }
        >
          {label}
        </button>
      ))}
    </div>
  );
}
